import { type ReactNode, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useStreakEngineEnabled } from '@/hooks/use-streak-engine-enabled';
import type { CollectorSummary } from '../types';
import {
  pickBannerState,
  type BannerSeverity,
  type BannerState,
} from '../utils/failure-banner';

const TONE: Record<BannerSeverity, string> = {
  banned: 'bg-error/10 border-error/40 text-error',
  error: 'bg-error/10 border-error/40 text-error',
  warning: 'bg-error/10 border-error/40 text-error',
  info: 'bg-white/[0.03] border-white/[0.08] text-text-tertiary',
};

function DismissButton({ onDismiss }: { onDismiss: () => void }): ReactNode {
  return (
    <button
      type="button"
      onClick={onDismiss}
      aria-label="Descartar aviso"
      className="shrink-0 opacity-60 hover:opacity-100"
    >
      <X size={14} />
    </button>
  );
}

function BannerView({
  state,
  onDismiss,
}: {
  state: BannerState;
  onDismiss: () => void;
}): ReactNode {
  return (
    <div
      role="status"
      className={cn(
        'flex items-center gap-sm rounded-xl border px-base py-sm',
        TONE[state.severity]
      )}
    >
      <p className="flex-1 text-[12px] leading-relaxed">{state.message}</p>
      {state.dismissable && <DismissButton onDismiss={onDismiss} />}
    </div>
  );
}

export function FailureStateBanner({
  summary,
}: {
  summary: CollectorSummary;
}): ReactNode {
  const engineEnabled = useStreakEngineEnabled();
  const [dismissed, setDismissed] = useState(false);
  const state = pickBannerState({ summary });
  if (!state || dismissed) return null;
  // Fase 32: motor off silences operational warning/error banners. 'banned'
  // is account state, not engine state — it still surfaces.
  if (!engineEnabled && state.severity !== 'banned') return null;
  return <BannerView state={state} onDismiss={() => setDismissed(true)} />;
}
